import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
    ArrowLeft,
    Plus,
    Edit,
    Trash2,
    Calendar,
    Clock,
    MapPin,
    DollarSign,
    TrendingUp,
    Users,
    Settings as SettingsIcon,
    Power,
    Home,
    Store as StoreIcon,
    Upload,
    X as XIcon,
    Image as ImageIcon,
    Eye,
    Loader2,
    Map
} from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import AddressFormGroup from "../components/AddressFormGroup";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ArtisanDashboardPage() {
    const { storeId } = useParams();
    const navigate = useNavigate();

    // --- State ---
    const [store, setStore] = useState(null);
    const [services, setServices] = useState([]);
    const [appointments, setAppointments] = useState([]);

    // Stats
    const [stats, setStats] = useState({
        appointments_month: 0,
        revenue_month: 0,
        occupation_rate: 0,
        average_rating: 0
    });

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("overview");
    const [appointmentFilter, setAppointmentFilter] = useState("today"); // New: today, week, month

    // Calendar State
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [blockedDates, setBlockedDates] = useState([]); // Array of strings "YYYY-MM-DD"
    const [blockDateReason, setBlockDateReason] = useState("");
    const [isBlockDateDialogOpen, setIsBlockDateDialogOpen] = useState(false);

    // Service Form State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [serviceForm, setServiceForm] = useState({
        name: "",
        description: "",
        price: "",
        duration: 60, // minutes
        buffer: 15, // minutes
        is_active: true,
        home_service: false,
        price_per_km: 0,
        free_zone_radius: 0,
        images: [],
        is_hourly: false
    });

    // Horaires hebdomadaires globaux de la boutique (sauvegardés dans service_config.availability)
    const DEFAULT_WEEK = [
        { day_of_week: 0, day_name: "Lundi",    start_time: "09:00", end_time: "17:00", is_available: true  },
        { day_of_week: 1, day_name: "Mardi",    start_time: "09:00", end_time: "17:00", is_available: true  },
        { day_of_week: 2, day_name: "Mercredi", start_time: "09:00", end_time: "17:00", is_available: true  },
        { day_of_week: 3, day_name: "Jeudi",    start_time: "09:00", end_time: "17:00", is_available: true  },
        { day_of_week: 4, day_name: "Vendredi", start_time: "09:00", end_time: "17:00", is_available: true  },
        { day_of_week: 5, day_name: "Samedi",   start_time: "10:00", end_time: "15:00", is_available: true  },
        { day_of_week: 6, day_name: "Dimanche", start_time: "09:00", end_time: "17:00", is_available: false },
    ];
    const [storeAvailability, setStoreAvailability] = useState(DEFAULT_WEEK);
    const [savingAvailability, setSavingAvailability] = useState(false);

    const handleSaveStoreAvailability = async () => {
        setSavingAvailability(true);
        try {
            const token = localStorage.getItem("buymoreeasy_token");
            await axios.put(`${API_URL}/stores/${storeId}`, {
                service_config: {
                    ...settingsForm,
                    availability: storeAvailability,
                    blocked_dates: blockedDates,
                    vacation_mode: settingsForm.vacation_mode,
                }
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Horaires sauvegardés !");
        } catch { toast.error("Erreur lors de la sauvegarde"); }
        finally { setSavingAvailability(false); }
    };

    // Availability State (per-service, dans le dialog création)
    const [editingServiceId, setEditingServiceId] = useState(null);
    const [availability, setAvailability] = useState([
        { day_of_week: 0, day_name: "Lundi", start_time: "09:00", end_time: "17:00", is_available: true },
        { day_of_week: 1, day_name: "Mardi", start_time: "09:00", end_time: "17:00", is_available: true },
        { day_of_week: 2, day_name: "Mercredi", start_time: "09:00", end_time: "17:00", is_available: true },
        { day_of_week: 3, day_name: "Jeudi", start_time: "09:00", end_time: "17:00", is_available: true },
        { day_of_week: 4, day_name: "Vendredi", start_time: "09:00", end_time: "17:00", is_available: true },
        { day_of_week: 5, day_name: "Samedi", start_time: "10:00", end_time: "15:00", is_available: true },
        { day_of_week: 6, day_name: "Dimanche", start_time: "09:00", end_time: "17:00", is_available: false }
    ]);

    // Settings State
    const [showLocationEdit, setShowLocationEdit] = useState(false);
    const [settingsForm, setSettingsForm] = useState({
        // Profile
        name: "",
        description: "",
        logo_url: "",
        banner_url: "",
        phone: "",
        address: "",
        city: "",
        region: "",
        zipCode: "",
        countryCode: "ht",
        address_details: "",

        // Config
        working_hours_start: "09:00",
        working_hours_end: "17:00",
        service_zone_radius: 10,
        service_location_type: "both",
        parallel_slots: 1,

        // Price per KM
        price_per_km: 0,
        free_zone_radius: 5, // Default 5km free

        // Geolocation
        coordinates: null, // { lat: ..., lon: ... }

        // Status
        vacation_mode: false
    });
    const [geocodingLoading, setGeocodingLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const fetchDashboardData = React.useCallback(async () => {
        setLoading(true);
        try {
            // Get auth token
            const token = localStorage.getItem('buymoreeasy_token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // Parallel fetching with authentication
            const [storeRes, servicesRes, apptRes] = await Promise.all([
                axios.get(`${API_URL}/stores/${storeId}`, { headers }),
                axios.get(`${API_URL}/stores/${storeId}/services`, { headers }),
                axios.get(`${API_URL}/stores/${storeId}/appointments`, { headers })
            ]);

            const s = storeRes.data;
            setStore(s);

            // Populate Settings Form
            setSettingsForm({
                name: s.name || "",
                description: s.description || "",
                logo_url: s.logo_url || "",
                banner_url: s.banner_url || "",
                phone: s.phone || "",
                address: s.address || "",
                city: s.city || "",
                region: s.region || "",
                zipCode: s.zip_code || "",
                countryCode: s.country_code || "ht",
                address_details: s.address_details || "",

                working_hours_start: s.service_config?.working_hours?.start || "09:00",
                working_hours_end: s.service_config?.working_hours?.end || "17:00",
                service_zone_radius: s.service_config?.service_zone_radius || 10,
                service_location_type: s.service_config?.service_location_type || "both",
                parallel_slots: s.service_config?.parallel_slots || 1,

                // Load price per km and free radius, fallback to defaults
                price_per_km: s.service_config?.price_per_km || 0,
                free_zone_radius: s.service_config?.free_zone_radius || 5,

                coordinates: s.coordinates || null,
                vacation_mode: s.service_config?.vacation_mode || false
            });

            // Load blocked dates
            setBlockedDates(s.service_config?.blocked_dates || []);

            // Load store-level weekly availability (service_config.availability)
            if (s.service_config?.availability?.length === 7) {
                setStoreAvailability(s.service_config.availability);
            }

            // Services
            setServices(servicesRes.data || []);

            // Appointments - Backend already returns flattened structure
            const serviceAppointments = (apptRes.data || []).map(apt => ({
                id: apt.id,
                itemId: apt.itemId,
                orderNumber: apt.orderNumber,
                status: apt.status ? apt.status.toLowerCase() : 'unknown',

                // Service Details
                serviceName: apt.serviceName || "Service",
                serviceImage: null, // Not provided by backend yet

                // Booking Times
                booking_date: apt.booking_date ? new Date(apt.booking_date) : null,
                booking_start: apt.booking_start ? new Date(apt.booking_start) : null,
                booking_end: apt.booking_end ? new Date(apt.booking_end) : null,
                bookingDate: apt.booking_date ? new Date(apt.booking_date) : new Date(),
                date: apt.booking_date ? format(new Date(apt.booking_date), "dd/MM/yyyy") : "-",
                time: apt.booking_date ? format(new Date(apt.booking_date), "HH:mm") : "-",
                timeEnd: apt.booking_end ? format(new Date(apt.booking_end), "HH:mm") : null,

                // Location
                service_location: apt.service_location || "En boutique",

                // Pricing
                price: apt.price || 0,
                travel_fee: apt.travel_fee || 0,
                total: (apt.price || 0) + (apt.travel_fee || 0),

                // Customer
                customer: apt.customer,
                client_name: apt.customer ? `${apt.customer.first_name} ${apt.customer.last_name}` : "Client",

                // Payment
                payment_method: apt.payment_method || "N/A",
                payment_status: apt.payment_status || "N/A"
            }));

            setAppointments(serviceAppointments);

            // Stats
            const totalRevenue = serviceAppointments.reduce((acc, app) => acc + (app.price || 0), 0);
            setStats({
                appointments_month: serviceAppointments.length,
                revenue_month: totalRevenue,
                occupation_rate: 0, // Placeholder
                average_rating: 0   // Placeholder
            });

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            toast.error("Erreur lors du chargement des données: " + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    // --- Effects & Data Fetching ---
    useEffect(() => {
        if (storeId) {
            fetchDashboardData();
        }
    }, [storeId, fetchDashboardData]);


    // --- Handlers ---

    // File Upload Handler
    const handleFileUpload = async (event, field) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setUploading(true);
        try {
            const folder = field === "legal_docs_url" ? "documents" : "stores";
            const res = await axios.post(`${API_URL}/uploads/?folder=${folder}`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setSettingsForm(prev => ({
                ...prev,
                [field]: res.data.url
            }));
            toast.success("Image téléchargée !");
        } catch (e) {
            console.error("Upload error", e);
            toast.error("Erreur lors du téléchargement");
        } finally {
            setUploading(false);
        }
    };

    // ... (rest of handlers)

    // Service & Availability Handlers
    const handleAddImage = () => {
        if (serviceForm.images.length >= 5) {
            toast.error("Maximum 5 photos par service");
            return;
        }
        const url = prompt("Entrez l'URL de l'image (PNG, JPG, GIF):");
        if (url) {
            setServiceForm({ ...serviceForm, images: [...serviceForm.images, url] });
        }
    };

    const handleRemoveImage = (index) => {
        const newImages = serviceForm.images.filter((_, i) => i !== index);
        setServiceForm({ ...serviceForm, images: newImages });
    };

    const handleEditService = async (service) => {
        setEditingServiceId(service.id);

        // Basic form population
        setServiceForm({
            name: service.title,
            description: service.description || "",
            price: service.price,
            duration: service.service_details?.duration || 60,
            buffer: service.service_details?.buffer || 15,
            is_active: service.is_active !== false,
            home_service: service.service_details?.home_service || false,
            price_per_km: service.service_details?.price_per_km || 0,
            free_zone_radius: service.service_details?.free_zone_radius || 0,
            images: service.images || [],
            is_hourly: service.is_hourly || false
        });

        // Fetch details to get availability spec
        try {
            const res = await axios.get(`${API_URL}/products/${service.id}`);
            const p = res.data;
            if (p.specifications && p.specifications.availability) {
                const loadedAvail = p.specifications.availability;
                setAvailability(prev => prev.map(day => {
                    const found = loadedAvail.find(a => a.day_of_week === day.day_of_week);
                    return found ? { ...day, ...found } : day;
                }));
            }
        } catch (e) { console.error("Error fetching full details", e); }

        setDialogOpen(true);
    };

    const updateAvailability = (index, field, value) => {
        setAvailability(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const handleDeleteService = async (serviceId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce service ?")) return;
        try {
            await axios.delete(`${API_URL}/products/${serviceId}`);
            toast.success("Service supprimé");
            fetchDashboardData();
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleCreateService = async () => {
        try {
            if (!serviceForm.name || !serviceForm.price) {
                toast.error("Nom et prix sont requis");
                return;
            }

            const productData = {
                title: serviceForm.name,
                description: serviceForm.description,
                price: parseFloat(serviceForm.price),
                images: serviceForm.images,
                is_active: true,
                product_type: "SERVICE", // Standardized to UPPERCASE
                status: "ACTIVE",
                stock_quantity: 999,
                service_details: {
                    duration: serviceForm.duration,
                    buffer: serviceForm.buffer,
                    home_service: serviceForm.home_service,
                    price_per_km: serviceForm.price_per_km,
                    free_zone_radius: serviceForm.free_zone_radius
                },
                specifications: {
                    availability: availability.map(a => ({
                        day_of_week: a.day_of_week,
                        start_time: a.start_time,
                        end_time: a.end_time,
                        is_available: a.is_available
                    }))
                },
                is_hourly: serviceForm.is_hourly
            };

            if (editingServiceId) {
                await axios.put(`${API_URL}/products/${editingServiceId}`, productData);
                toast.success("Service modifié !");
            } else {
                await axios.post(`${API_URL}/stores/${storeId}/products`, productData);
                toast.success("Service créé !");
            }

            // Reset
            setServiceForm({
                name: "",
                description: "",
                price: "",
                duration: 60,
                buffer: 15,
                is_active: true,
                home_service: false,
                price_per_km: 0,
                free_zone_radius: 0,
                images: [],
                is_hourly: false
            });
            setEditingServiceId(null);
            setDialogOpen(false);
            fetchDashboardData();
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    // Settings Handlers
    const handleGeolocate = async () => {
        if (!settingsForm.address || !settingsForm.city) {
            toast.error("Veuillez entrer l'adresse et la ville");
            return;
        }

        setGeocodingLoading(true);
        try {
            const query = `${settingsForm.address}, ${settingsForm.city}`;
            const res = await axios.get(`https://nominatim.openstreetmap.org/search`, {
                params: {
                    q: query,
                    format: "json",
                    limit: 1
                }
            });

            if (res.data && res.data.length > 0) {
                const { lat, lon } = res.data[0];
                setSettingsForm(prev => ({
                    ...prev,
                    coordinates: { lat: parseFloat(lat), lon: parseFloat(lon) }
                }));
                toast.success("Coordonnées trouvées !");
            } else {
                toast.error("Impossible de trouver l'adresse. Essayez d'être plus précis.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Erreur de géolocalisation");
        } finally {
            setGeocodingLoading(false);
        }
    };

    // ...

    const handleSaveSettings = async () => {
        try {
            // Validate required
            if (!settingsForm.name) {
                toast.error("Le nom de la boutique est requis");
                return;
            }

            const updateData = {
                name: settingsForm.name,
                description: settingsForm.description,
                logo_url: settingsForm.logo_url,
                banner_url: settingsForm.banner_url,
                phone: settingsForm.phone,
                address: settingsForm.address,
                city: settingsForm.city,
                region: settingsForm.region,
                zip_code: settingsForm.zipCode,
                country_code: settingsForm.countryCode,
                address_details: settingsForm.address_details,
                coordinates: settingsForm.coordinates || null, // Ensure not undefined

                service_config: {
                    working_hours: {
                        start: settingsForm.working_hours_start,
                        end: settingsForm.working_hours_end
                    },
                    service_zone_radius: settingsForm.service_zone_radius,
                    service_location_type: settingsForm.service_location_type,
                    parallel_slots: settingsForm.parallel_slots,
                    vacation_mode: settingsForm.vacation_mode,

                    // New Fields
                    price_per_km: parseFloat(settingsForm.price_per_km) || 0,
                    free_zone_radius: parseFloat(settingsForm.free_zone_radius) || 0
                }
            };

            await axios.patch(`${API_URL}/stores/${storeId}`, updateData);
            toast.success("Paramètres sauvegardés");
            fetchDashboardData();
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de la sauvegarde: " + (e.response?.data?.detail || e.message));
        }
    };


    const handleDeleteStore = async () => {
        try {
            await axios.delete(`${API_URL}/stores/${storeId}`);
            toast.success("Boutique supprimée");
            navigate("/my-stores");
        } catch (e) {
            toast.error("Erreur lors du suppression");
        }
    };

    const handleBlockDate = async () => {
        try {
            const dateStr = format(selectedDate, "yyyy-MM-dd");
            let newBlockedDates;
            const isBlocked = blockedDates.includes(dateStr);

            if (isBlocked) {
                // Unblock
                newBlockedDates = blockedDates.filter(d => d !== dateStr);
            } else {
                // Block
                newBlockedDates = [...blockedDates, dateStr];
            }

            // Optimistic update
            setBlockedDates(newBlockedDates);

            const currentConfig = store.service_config || {};
            const updatedConfig = {
                ...currentConfig,
                blocked_dates: newBlockedDates
            };

            const token = localStorage.getItem('buymoreeasy_token');
            await axios.patch(`${API_URL}/stores/${storeId}`, {
                service_config: updatedConfig
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(isBlocked ? "Date débloquée" : "Date bloquée");
            setIsBlockDateDialogOpen(false);
            fetchDashboardData();
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la mise à jour");
        }
    };

    // Appointment Status Handler
    const handleUpdateOrderStatus = async (itemId, newStatus) => {
        try {
            // Use the specific item status endpoint instead of global order status
            await axios.put(`${API_URL}/store-orders/items/${itemId}/status`, { status: newStatus });
            toast.success(`Statut mis à jour !`);
            fetchDashboardData();
        } catch (error) {
            console.error("Error updating status:", error);
            const msg = error.response?.data?.detail || "Erreur lors de la mise à jour";
            toast.error(msg);
        }
    };

    // ... (rest of code)


    // Filter appointments by period
    const filteredAppointments = appointments.filter(app => {
        const appDate = app.bookingDate;
        const now = new Date();

        if (appointmentFilter === 'today') {
            return appDate.toDateString() === now.toDateString();
        } else if (appointmentFilter === 'week') {
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
            weekStart.setHours(0, 0, 0, 0);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 7);
            return appDate >= weekStart && appDate < weekEnd;
        } else { // month
            return appDate.getMonth() === now.getMonth() &&
                appDate.getFullYear() === now.getFullYear();
        }
    }).sort((a, b) => a.bookingDate - b.bookingDate); // Sort by date

    const appointmentsForSelectedDate = appointments.filter(app =>
        app.bookingDate.toDateString() === selectedDate?.toDateString()
    );

    if (loading || !store) {
        return (
            <div className="min-h-screen bg-background">
                <Navbar />
                <div className="container mx-auto px-4 py-8 flex justify-center items-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                    <p className="text-gray-600">{loading ? 'Chargement...' : 'Impossible de charger la boutique. Vérifiez votre connexion.'}</p>
                </div>
            </div>
        );
    }

    // --- Render ---
    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            <Navbar />

            <main className="container mx-auto px-4 py-8 max-w-7xl">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate("/my-stores")}>
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold">{store?.name}</h1>
                            <p className="text-sm text-muted-foreground">Dashboard Artisan</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate(`/boutique/${store?.slug || storeId}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Voir ma boutique
                        </Button>
                        <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                            <StoreIcon className="h-3.5 w-3.5 mr-1" />
                            Artisan
                        </Badge>
                    </div>
                </div>

                {store?.status === 'REJECTED' && (
                    <div className="bg-red-50 border border-red-200 px-6 py-4 mb-6 rounded-lg flex items-center justify-between">
                        <div className="flex items-start gap-3">
                            <XIcon className="h-5 w-5 text-red-600 mt-1" />
                            <div>
                                <h3 className="font-bold text-red-800">Demande Rejetée</h3>
                                <p className="text-sm text-red-700">
                                    <strong>Raison :</strong> {store.rejection_reason || "Documents manquants ou invalides."}
                                    <br />
                                    <span className="text-xs opacity-75">Les frais d&apos;activation ont été remboursés. Corrigez les informations et payez à nouveau pour soumettre.</span>
                                </p>
                            </div>
                        </div>
                        <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => navigate(`/store-payment/${store.id}`)}>
                            Payer & Renvoyer
                        </Button>
                    </div>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="overview">Vue d&apos;ensemble</TabsTrigger>
                        <TabsTrigger value="calendar">Calendrier</TabsTrigger>
                        <TabsTrigger value="services">Services</TabsTrigger>
                        <TabsTrigger value="settings">Paramètres</TabsTrigger>
                    </TabsList>

                    {/* 1. Overview Tab - Planning Style */}
                    <TabsContent value="overview" className="space-y-6">
                        {/* Filter Buttons */}
                        <div className="flex items-center justify-between">
                            <div className="flex gap-2">
                                {['today', 'week', 'month'].map(period => (
                                    <Button
                                        key={period}
                                        variant={appointmentFilter === period ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setAppointmentFilter(period)}
                                    >
                                        <Calendar className="w-4 h-4 mr-2" />
                                        {period === 'today' ? "Aujourd'hui" : period === 'week' ? 'Cette semaine' : 'Ce mois'}
                                    </Button>
                                ))}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
                            </div>
                        </div>

                        {/* Quick Stats Row */}
                        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-blue-600 rounded-lg">
                                        <Calendar className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-blue-900">Total</span>
                                </div>
                                <div className="text-3xl font-bold text-blue-600">{filteredAppointments.length}</div>
                            </div>
                            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-xl border border-amber-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-amber-600 rounded-lg">
                                        <Clock className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-amber-900">En attente</span>
                                </div>
                                <div className="text-3xl font-bold text-amber-600">
                                    {filteredAppointments.filter(a => a.status !== 'CANCELLED' && a.status !== 'DELIVERED').length}
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-green-600 rounded-lg">
                                        <TrendingUp className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-green-900">Terminés</span>
                                </div>
                                <div className="text-3xl font-bold text-green-600">
                                    {filteredAppointments.filter(a => a.status === 'DELIVERED').length}
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-purple-600 rounded-lg">
                                        <DollarSign className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-purple-900">Revenus</span>
                                </div>
                                <div className="text-2xl font-bold text-purple-600">
                                    {filteredAppointments.reduce((acc, app) => acc + (app.price || 0), 0)} HTG
                                </div>
                            </div>
                        </div>

                        {/* Planning Timeline */}
                        <div className="bg-white rounded-xl border shadow-sm">
                            <div className="p-6 border-b bg-gradient-to-r from-gray-50 to-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">
                                            {appointmentFilter === 'today' ? "📅 Planning d'aujourd'hui" :
                                                appointmentFilter === 'week' ? '📅 Planning de la semaine' : '📅 Planning du mois'}
                                        </h2>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {filteredAppointments.length} rendez-vous programmé{filteredAppointments.length > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    {filteredAppointments.length > 0 && (
                                        <Button variant="outline" onClick={() => setActiveTab('calendar')} className="gap-2">
                                            <Eye className="w-4 h-4" />
                                            Vue calendrier
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="p-6">
                                {filteredAppointments.length === 0 ? (
                                    <div className="text-center py-16">
                                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                                            <Calendar className="w-10 h-10 text-gray-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun rendez-vous</h3>
                                        <p className="text-gray-600">
                                            {appointmentFilter === 'today' ? "Profitez de cette journée tranquille ! 😌" :
                                                appointmentFilter === 'week' ? "Aucun rendez-vous prévu cette semaine" :
                                                    "Aucun rendez-vous prévu ce mois"}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredAppointments.map((apt, idx) => {
                                            const initials = apt.client_name.split(' ').map(n => n[0]).join('').toUpperCase();
                                            const isScheduled   = apt.status === 'SCHEDULED';
                                            const isPending     = apt.status === 'PENDING';
                                            const isInProgress  = apt.status === 'IN_PROGRESS';
                                            const isCompleted   = apt.status === 'DELIVERED' || apt.status === 'COMPLETED';
                                            const isCancelled   = apt.status === 'CANCELLED';
                                            const isUpcoming    = !isCompleted && !isCancelled;
                                            const hasTimeRange = apt.timeEnd && apt.timeEnd !== apt.time;
                                            const isHomeService = apt.service_location && apt.service_location !== "En boutique";

                                            return (
                                                <div
                                                    key={apt.id + apt.itemId}
                                                    className={`relative group transition-all duration-200 ${
                                                        isCompleted   ? 'bg-green-50 border-green-200' :
                                                        isCancelled   ? 'bg-gray-50 border-gray-200 opacity-60' :
                                                        isInProgress  ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 hover:shadow-lg hover:scale-[1.02]' :
                                                        isScheduled   ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300 hover:shadow-lg hover:scale-[1.02]' :
                                                        'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:shadow-lg hover:scale-[1.02]'
                                                    } border-2 rounded-2xl p-5`}
                                                >
                                                    {/* Time Badge - Left Side */}
                                                    <div className="absolute -left-3 top-1/2 -translate-y-1/2 bg-white border-2 border-current rounded-full px-3 py-2 shadow-md">
                                                        <div className={`text-center ${isCompleted ? 'text-green-600' :
                                                            isCancelled ? 'text-gray-400' :
                                                                'text-blue-600'
                                                            }`}>
                                                            <Clock className="w-4 h-4 mx-auto mb-1" />
                                                            <div className="text-xs font-bold whitespace-nowrap">
                                                                {apt.time}
                                                                {hasTimeRange && (
                                                                    <>
                                                                        <br />
                                                                        <span className="text-[10px]">↓</span>
                                                                        <br />
                                                                        {apt.timeEnd}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Main Content */}
                                                    <div className="ml-16">
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex items-start gap-4 flex-1">
                                                                {/* Avatar */}
                                                                <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg ${isCompleted ? 'bg-green-600 text-white' :
                                                                    isCancelled ? 'bg-gray-300 text-gray-600' :
                                                                        'bg-gradient-to-br from-blue-600 to-purple-600 text-white'
                                                                    } shadow-lg`}>
                                                                    {isCompleted ? '✓' : initials}
                                                                </div>

                                                                {/* Client Info */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-3 mb-2">
                                                                        <h3 className="text-xl font-bold text-gray-900">{apt.client_name}</h3>
                                                                        {isCompleted && (
                                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
                                                                                <TrendingUp className="w-3 h-3" /> Terminé
                                                                            </span>
                                                                        )}
                                                                        {isCancelled && (
                                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-400 text-white">
                                                                                Annulé
                                                                            </span>
                                                                        )}
                                                                        {isScheduled && (
                                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white animate-pulse">
                                                                                <Calendar className="w-3 h-3" /> Confirmé
                                                                            </span>
                                                                        )}
                                                                        {isPending && (
                                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500 text-white">
                                                                                <Clock className="w-3 h-3" /> En attente paiement
                                                                            </span>
                                                                        )}
                                                                        {isInProgress && (
                                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-purple-600 text-white animate-pulse">
                                                                                <TrendingUp className="w-3 h-3" /> En cours
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {/* Service Name */}
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <Users className="w-4 h-4 text-purple-600" />
                                                                        <span className="font-semibold text-gray-800">{apt.serviceName}</span>
                                                                    </div>

                                                                    {/* Service Details Grid */}
                                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600 mb-2">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                                                            <span>{apt.date}</span>
                                                                        </div>

                                                                        {hasTimeRange && (
                                                                            <div className="flex items-center gap-1.5">
                                                                                <Clock className="w-3.5 h-3.5 text-blue-600" />
                                                                                <span>{apt.time} - {apt.timeEnd}</span>
                                                                            </div>
                                                                        )}

                                                                        {isHomeService && (
                                                                            <div className="flex items-center gap-1.5 col-span-2">
                                                                                <MapPin className="w-3.5 h-3.5 text-orange-600" />
                                                                                <span className="truncate" title={apt.service_location}>
                                                                                    {apt.service_location}
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        {!isHomeService && (
                                                                            <div className="flex items-center gap-1.5">
                                                                                <Home className="w-3.5 h-3.5 text-blue-600" />
                                                                                <span>En boutique</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Pricing Details */}
                                                                    <div className="flex items-center gap-4 text-sm mt-2">
                                                                        <div className="flex items-center gap-1.5 font-semibold text-purple-600">
                                                                            <DollarSign className="w-4 h-4" />
                                                                            <span>Service: {apt.price} HTG</span>
                                                                        </div>
                                                                        {apt.travel_fee > 0 && (
                                                                            <div className="flex items-center gap-1.5 text-orange-600">
                                                                                <MapPin className="w-4 h-4" />
                                                                                <span>Déplacement: +{apt.travel_fee} HTG</span>
                                                                            </div>
                                                                        )}
                                                                        {apt.travel_fee > 0 && (
                                                                            <div className="flex items-center gap-1.5 font-bold text-gray-900">
                                                                                <span>Total: {apt.total} HTG</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Order Number */}
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        Commande #{apt.orderNumber}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Action Button */}
                                                            <div className="flex-shrink-0">
                                                                {/* SCHEDULED ou PENDING → Démarrer le service */}
                                                                {(isScheduled || isPending) && (
                                                                    <Button
                                                                        size="lg"
                                                                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg"
                                                                        onClick={() => {
                                                                            if (!window.confirm(`Démarrer le service pour ${apt.client_name} ?`)) return;
                                                                            handleUpdateOrderStatus(apt.itemId, 'IN_PROGRESS');
                                                                        }}
                                                                    >
                                                                        <TrendingUp className="w-5 h-5 mr-2" />
                                                                        Démarrer
                                                                    </Button>
                                                                )}
                                                                {/* IN_PROGRESS → Terminer */}
                                                                {isInProgress && (
                                                                    <Button
                                                                        size="lg"
                                                                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg"
                                                                        onClick={() => {
                                                                            if (!window.confirm(`Confirmer la fin du service pour ${apt.client_name} ?`)) return;
                                                                            handleUpdateOrderStatus(apt.itemId, 'DELIVERED');
                                                                        }}
                                                                    >
                                                                        <TrendingUp className="w-5 h-5 mr-2" />
                                                                        Terminer
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* 2. Calendar Tab */}
                    <TabsContent value="calendar" className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                            {/* Calendar Component */}
                            <Card className="md:col-span-4 lg:col-span-3 h-fit sticky top-6">
                                <CardContent className="p-4 flex flex-col items-center">
                                    <CalendarComponent
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={setSelectedDate}
                                        locale={fr}
                                        className="rounded-md border shadow-sm w-full"
                                        modifiers={{
                                            booked: (date) => appointments.some(app => app.bookingDate.toDateString() === date.toDateString()),
                                            hasPending: (date) => appointments.some(app => app.bookingDate.toDateString() === date.toDateString() && app.status !== 'DELIVERED' && app.status !== 'CANCELLED'),
                                            hasCompleted: (date) => appointments.some(app => app.bookingDate.toDateString() === date.toDateString() && app.status === 'DELIVERED'),
                                            blocked: (date) => blockedDates.includes(format(date, "yyyy-MM-dd"))
                                        }}
                                        modifiersStyles={{
                                            booked: { fontWeight: 'bold' },
                                            hasPending: { borderBottom: '2px solid #f59e0b' }, // Amber for pending
                                            hasCompleted: { borderBottom: '2px solid #10b981' }, // Green for completed
                                            blocked: { backgroundColor: '#fee2e2', color: '#991b1b', textDecoration: 'line-through' } // Red for blocked
                                        }}
                                    />

                                    {/* Legend */}
                                    <div className="w-full mt-6 space-y-3 pt-4 border-t">
                                        <h4 className="text-sm font-semibold text-gray-700">Légende</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                                <span className="text-gray-600">En attente / Confirmé</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                                <span className="text-gray-600">Terminé</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-red-100 border border-red-200"></div>
                                                <span className="text-gray-600">Bloqué / Indisponible</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full border border-gray-300 bg-gray-50"></div>
                                                <span className="text-gray-600">Jour libre</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="w-full mt-6 space-y-2">
                                        <Button
                                            variant={blockedDates.includes(format(selectedDate, "yyyy-MM-dd")) ? "secondary" : "destructive"}
                                            className="w-full justify-start text-left"
                                            onClick={handleBlockDate}
                                        >
                                            {blockedDates.includes(format(selectedDate, "yyyy-MM-dd")) ? (
                                                <>
                                                    <Calendar className="w-4 h-4 mr-2" />
                                                    Débloquer cette date
                                                </>
                                            ) : (
                                                <>
                                                    <XIcon className="w-4 h-4 mr-2" />
                                                    Bloquer cette date
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Appointments List for Date */}
                            <Card className="md:col-span-8 lg:col-span-9">
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        Rendez-vous du {selectedDate ? format(selectedDate, "d MMMM yyyy", { locale: fr }) : "..."}
                                        {blockedDates.includes(format(selectedDate, "yyyy-MM-dd")) && (
                                            <Badge variant="destructive" className="ml-4">BLOQUÉ</Badge>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {blockedDates.includes(format(selectedDate, "yyyy-MM-dd")) && (
                                        <div className="flex flex-col items-center justify-center p-8 mb-6 text-center bg-red-50 rounded-lg border border-red-100">
                                            <XIcon className="w-12 h-12 text-red-300 mb-3" />
                                            <h3 className="text-lg font-medium text-red-800">Journée indisponible</h3>
                                            <p className="text-red-600">Vous avez bloqué cette date pour les réservations.</p>
                                        </div>
                                    )}

                                    {appointmentsForSelectedDate.length > 0 ? (
                                        <div className="space-y-4">
                                            {appointmentsForSelectedDate.map((app, idx) => {
                                                const hasTimeRange = app.timeEnd && app.timeEnd !== app.time;
                                                const isHomeService = app.service_location && app.service_location !== "En boutique";
                                                const isCompleted = app.status === 'COMPLETED' || app.status === 'DELIVERED';
                                                const isCancelled = app.status === 'CANCELLED';
                                                const isUpcoming = !isCompleted && !isCancelled;

                                                return (
                                                    <div key={idx} className="flex flex-col p-5 border-2 rounded-xl hover:bg-gray-50 transition-colors bg-white shadow-sm">
                                                        {/* Header Row */}
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex flex-col">
                                                                    <Badge variant="outline" className="mb-1">
                                                                        {hasTimeRange ? `${app.time} - ${app.timeEnd}` : app.time}
                                                                    </Badge>
                                                                    <span className="font-semibold text-lg text-gray-900">{app.client_name}</span>
                                                                </div>
                                                            </div>
                                                            <Badge variant={
                                                                app.status === 'COMPLETED' || app.status === 'DELIVERED' ? 'success' :
                                                                    app.status === 'CANCELLED' ? 'destructive' : 'default'
                                                            }>
                                                                {app.status === 'DELIVERED' ? 'Terminé' : app.status === 'CANCELLED' ? 'Annulé' : app.status}
                                                            </Badge>
                                                        </div>

                                                        {/* Service Info */}
                                                        <div className="space-y-2 mb-3">
                                                            <p className="text-gray-700 flex items-center gap-2">
                                                                <Users className="w-4 h-4 text-purple-600" />
                                                                <span className="font-medium">{app.serviceName}</span>
                                                            </p>

                                                            {isHomeService && (
                                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                                    <MapPin className="w-4 h-4 text-orange-600" />
                                                                    <span>{app.service_location}</span>
                                                                </p>
                                                            )}

                                                            {!isHomeService && (
                                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                                    <Home className="w-4 h-4 text-blue-600" />
                                                                    <span>Service en boutique</span>
                                                                </p>
                                                            )}
                                                        </div>

                                                        {/* Pricing */}
                                                        <div className="flex items-center gap-4 text-sm mb-3">
                                                            <span className="text-gray-600">Commande #{app.orderNumber}</span>
                                                            <span className="font-semibold text-purple-600">{app.price} HTG</span>
                                                            {app.travel_fee > 0 && (
                                                                <>
                                                                    <span className="text-orange-600">+ {app.travel_fee} HTG déplacement</span>
                                                                    <span className="font-bold text-gray-900">= {app.total} HTG</span>
                                                                </>
                                                            )}
                                                        </div>

                                                        {/* Actions */}
                                                        {app.status !== 'CANCELLED' && app.status !== 'DELIVERED' && (
                                                            <div className="flex gap-2 pt-3 border-t">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="text-red-500 border-red-200 hover:bg-red-50"
                                                                    onClick={() => handleUpdateOrderStatus(app.itemId, 'CANCELLED')}
                                                                >
                                                                    Annuler
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-green-600 hover:bg-green-700 text-white"
                                                                    onClick={() => handleUpdateOrderStatus(app.itemId, 'DELIVERED')}
                                                                >
                                                                    <TrendingUp className="w-4 h-4 mr-1" />
                                                                    Terminer
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-gray-500">
                                            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                            <p>Aucun rendez-vous prévu pour cette date.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* 3. Services Tab */}
                    <TabsContent value="services" className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold">Mes Services</h2>
                            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button onClick={() => setEditingServiceId(null)}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Ajouter un Service
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>{editingServiceId ? "Modifier le Service" : "Nouveau Service"}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto px-1">
                                        <div>
                                            <Label>Nom du service *</Label>
                                            <Input
                                                value={serviceForm.name}
                                                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                                                placeholder="Ex: Coupe Homme"
                                            />
                                        </div>
                                        <div>
                                            <Label>Description</Label>
                                            <Textarea
                                                value={serviceForm.description}
                                                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                                                placeholder="Décrivez votre service..."
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label>Prix (HTG) *</Label>
                                                <Input
                                                    type="number"
                                                    value={serviceForm.price}
                                                    onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <Label>Durée (minutes) *</Label>
                                                <div className="flex gap-2">
                                                    <Select
                                                        value={serviceForm.duration.toString()}
                                                        onValueChange={(v) => setServiceForm({ ...serviceForm, duration: parseInt(v) })}
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="30">30 min</SelectItem>
                                                            <SelectItem value="45">45 min</SelectItem>
                                                            <SelectItem value="60">1 heure</SelectItem>
                                                            <SelectItem value="90">1h 30min</SelectItem>
                                                            <SelectItem value="120">2 heures</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div>
                                                <Label>Pause après (min)</Label>
                                                <Input
                                                    type="number"
                                                    value={serviceForm.buffer}
                                                    onChange={(e) => setServiceForm({ ...serviceForm, buffer: parseInt(e.target.value) || 0 })}
                                                    placeholder="15"
                                                    min="0"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                                            <div className="space-y-0.5">
                                                <Label className="text-blue-900">Prix par heure</Label>
                                                <p className="text-xs text-blue-700">Permet au client de choisir une plage horaire (ex: 9h-11h)</p>
                                            </div>
                                            <Switch
                                                checked={serviceForm.is_hourly}
                                                onCheckedChange={(checked) => setServiceForm({ ...serviceForm, is_hourly: checked })}
                                            />
                                        </div>

                                        {/* Images Upload */}
                                        <div className="space-y-2">
                                            <Label>Photos du service (max 5)</Label>
                                            <div className="grid grid-cols-5 gap-2">
                                                {serviceForm.images.map((img, idx) => (
                                                    <div key={idx} className="relative group">
                                                        <img
                                                            src={img}
                                                            alt={`Service ${idx + 1}`}
                                                            className="w-full h-20 object-cover rounded border"
                                                        />
                                                        <button
                                                            onClick={() => handleRemoveImage(idx)}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                                        >
                                                            <XIcon className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                                {serviceForm.images.length < 5 && (
                                                    <button
                                                        onClick={handleAddImage}
                                                        className="w-full h-20 border-2 border-dashed rounded flex flex-col items-center justify-center hover:bg-gray-50 transition"
                                                    >
                                                        <Upload className="h-5 w-5 text-gray-400" />
                                                        <span className="text-xs text-gray-500 mt-1">Ajouter</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Home Service Configuration */}
                                        <div className="space-y-4 border-t pt-4">
                                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                                <div className="space-y-0.5">
                                                    <Label>Service à Domicile</Label>
                                                    <p className="text-xs text-muted-foreground">Proposez-vous ce service chez le client ?</p>
                                                </div>
                                                <Switch
                                                    checked={serviceForm.home_service}
                                                    onCheckedChange={(checked) => setServiceForm({ ...serviceForm, home_service: checked })}
                                                />
                                            </div>

                                            {serviceForm.home_service && (
                                                <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-gray-200">
                                                    <div>
                                                        <Label>Rayon gratuit (km)</Label>
                                                        <Input
                                                            type="number"
                                                            value={serviceForm.free_zone_radius}
                                                            onChange={(e) => setServiceForm({ ...serviceForm, free_zone_radius: parseFloat(e.target.value) })}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Prix par km (HTG)</Label>
                                                        <Input
                                                            type="number"
                                                            value={serviceForm.price_per_km}
                                                            onChange={(e) => setServiceForm({ ...serviceForm, price_per_km: parseFloat(e.target.value) })}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Active Status */}
                                        <div className="flex items-center justify-between p-3 border rounded-lg">
                                            <Label>Service Actif (Visible)</Label>
                                            <Switch
                                                checked={serviceForm.is_active}
                                                onCheckedChange={(checked) => setServiceForm({ ...serviceForm, is_active: checked })}
                                            />
                                        </div>

                                        {/* Availability Section */}
                                        <div className="space-y-2 border-t pt-4">
                                            <Label className="text-base font-semibold">Disponibilité</Label>
                                            <div className="space-y-2">
                                                {availability.map((day, idx) => (
                                                    <div key={day.day_of_week} className="flex items-center gap-2 text-sm">
                                                        <div className="w-24 flex items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={day.is_available}
                                                                onChange={(e) => updateAvailability(idx, 'is_available', e.target.checked)}
                                                                className="h-4 w-4"
                                                            />
                                                            <span>{day.day_name}</span>
                                                        </div>
                                                        {day.is_available ? (
                                                            <>
                                                                <Input
                                                                    type="time"
                                                                    value={day.start_time}
                                                                    onChange={(e) => updateAvailability(idx, 'start_time', e.target.value)}
                                                                    className="h-8 w-24"
                                                                />
                                                                <span>à</span>
                                                                <Input
                                                                    type="time"
                                                                    value={day.end_time}
                                                                    onChange={(e) => updateAvailability(idx, 'end_time', e.target.value)}
                                                                    className="h-8 w-24"
                                                                />
                                                            </>
                                                        ) : (
                                                            <span className="text-muted-foreground italic">Fermé</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <Button onClick={handleCreateService} className="w-full mt-4">
                                            {editingServiceId ? "Enregistrer les modifications" : "Créer le Service"}
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <Card>
                            <CardContent className="pt-6">
                                {services.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-muted-foreground mb-4">Aucun service créé</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Service</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead>Durée</TableHead>
                                                <TableHead>Prix</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {services.map(service => (
                                                <TableRow key={service.id}>
                                                    <TableCell className="font-medium">{service.title}</TableCell>
                                                    <TableCell>
                                                        {service.service_details?.home_service ? (
                                                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Domicile</Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Sur place</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {service.is_active !== false ? (
                                                            <Badge className="bg-green-600">Actif</Badge>
                                                        ) : (
                                                            <Badge variant="secondary">Inactif</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{service.service_details?.duration || service.duration || 60} min</TableCell>
                                                    <TableCell>{service.price} HTG</TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-2">
                                                            <Button variant="ghost" size="sm" onClick={() => handleEditService(service)}>
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteService(service.id)}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* 4. Settings Tab */}
                    <TabsContent value="settings" className="space-y-8">

                        {/* 4.1 Profile Settings */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Profil de la Boutique</CardTitle>
                                <CardDescription>Gérez les informations visibles par vos clients.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nom de la boutique</Label>
                                        <Input
                                            value={settingsForm.name}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Téléphone</Label>
                                        <Input
                                            value={settingsForm.phone}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })}
                                            placeholder="+509..."
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description / A propos</Label>
                                    <Textarea
                                        value={settingsForm.description}
                                        onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
                                        rows={4}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Logo</Label>
                                        <div className="flex gap-4 items-center">
                                            {settingsForm.logo_url && (
                                                <img src={settingsForm.logo_url} alt="Logo" className="w-16 h-16 rounded object-cover border" />
                                            )}
                                            <div className="flex-1">
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleFileUpload(e, "logo_url")}
                                                    disabled={uploading}
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">Format: JPG, PNG, GIF</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bannière</Label>
                                        <div className="space-y-2">
                                            {settingsForm.banner_url && (
                                                <img src={settingsForm.banner_url} alt="Bannière" className="w-full h-32 rounded object-cover border" />
                                            )}
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(e, "banner_url")}
                                                disabled={uploading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* 4.2 Location & Geocoding */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Adresses & Localisation</CardTitle>
                                <CardDescription>Indispensable pour le calcul des frais de déplacement.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <Label className="text-base font-semibold">Localisation de l'atelier / boutique</Label>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowLocationEdit(!showLocationEdit)}
                                            className="text-primary hover:text-primary/80"
                                        >
                                            {showLocationEdit ? (
                                                <><XIcon className="h-4 w-4 mr-1" /> Fermer</>
                                            ) : (
                                                <><Edit className="h-4 w-4 mr-1" /> Modifier la localisation</>
                                            )}
                                        </Button>
                                    </div>

                                    {showLocationEdit ? (
                                        <div className="bg-gray-50 p-4 rounded-xl border animate-in fade-in slide-in-from-top-2 duration-200">
                                            <AddressFormGroup
                                                addressData={{
                                                    countryCode: settingsForm.countryCode,
                                                    address: settingsForm.address,
                                                    city: settingsForm.city,
                                                    region: settingsForm.region,
                                                    zipCode: settingsForm.zipCode,
                                                    address_details: settingsForm.address_details,
                                                    lat: settingsForm.coordinates?.lat || null,
                                                    lng: settingsForm.coordinates?.lon || null
                                                }}
                                                onChange={(newData) => {
                                                    setSettingsForm(prev => ({
                                                        ...prev,
                                                        countryCode: newData.countryCode,
                                                        address: newData.address,
                                                        city: newData.city,
                                                        region: newData.region,
                                                        zipCode: newData.zipCode,
                                                        address_details: newData.address_details,
                                                        coordinates: (newData.lat && newData.lng) ? { lat: newData.lat, lon: newData.lng } : null
                                                    }));
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex items-start gap-3 text-sm text-gray-600 bg-gray-50/50 p-4 rounded-xl border border-dashed hover:bg-gray-50 transition-colors">
                                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 flex-shrink-0">
                                                <MapPin className="h-5 w-5 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900">{settingsForm.address || 'Aucune adresse renseignée'}</p>
                                                <p className="text-gray-500">{settingsForm.city}, {settingsForm.region} {settingsForm.zipCode && `(${settingsForm.zipCode})`}</p>
                                                {settingsForm.address_details && (
                                                    <p className="text-xs text-gray-400 mt-1 italic">{settingsForm.address_details}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                                    <div>
                                        <p className="font-medium text-sm flex items-center">
                                            <MapPin className="w-4 h-4 mr-2 text-primary" />
                                            Coordonnées GPS
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {settingsForm.coordinates
                                                ? `Lat: ${settingsForm.coordinates.lat}, Lon: ${settingsForm.coordinates.lon}`
                                                : "Non géolocalisé"}
                                        </p>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleGeolocate}
                                        disabled={geocodingLoading}
                                    >
                                        {geocodingLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Map className="w-4 h-4 mr-2" />}
                                        Géolocaliser
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* 4.3 Service Configuration */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Configuration des Services</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label className="text-base font-semibold">Horaires de Travail (Défaut)</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-sm">Ouverture</Label>
                                            <Input
                                                type="time"
                                                value={settingsForm.working_hours_start}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, working_hours_start: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-sm">Fermeture</Label>
                                            <Input
                                                type="time"
                                                value={settingsForm.working_hours_end}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, working_hours_end: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-base font-semibold">Zone & Frais de Déplacement</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-sm">Rayon gratuit (km)</Label>
                                            <p className="text-xs text-muted-foreground mb-1">Distance offerte au client</p>
                                            <Input
                                                type="number"
                                                value={settingsForm.free_zone_radius}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, free_zone_radius: parseFloat(e.target.value) })}
                                                placeholder="Ex: 5"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-sm">Prix par km (HTG)</Label>
                                            <p className="text-xs text-muted-foreground mb-1">Facturé au-delà du rayon gratuit</p>
                                            <Input
                                                type="number"
                                                value={settingsForm.price_per_km}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, price_per_km: parseFloat(e.target.value) })}
                                                placeholder="Ex: 25"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-sm">Rayon max / Zone de service (km)</Label>
                                            <p className="text-xs text-muted-foreground mb-1">Distance maximale d&apos;intervention</p>
                                            <Input
                                                type="number"
                                                value={settingsForm.service_zone_radius}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, service_zone_radius: parseFloat(e.target.value) })}
                                                placeholder="Ex: 15"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* 4.4 Horaires hebdomadaires (disponibilités boutique) */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-orange-600" />
                                    Horaires d'ouverture
                                </CardTitle>
                                <CardDescription>
                                    Définissez vos jours et heures de disponibilité par défaut. Ces horaires servent de base pour la génération des créneaux.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {storeAvailability.map((day, idx) => (
                                    <div key={day.day_of_week} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${day.is_available ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="flex items-center gap-2 w-28">
                                            <input
                                                type="checkbox"
                                                checked={day.is_available}
                                                onChange={e => {
                                                    const upd = [...storeAvailability];
                                                    upd[idx] = { ...upd[idx], is_available: e.target.checked };
                                                    setStoreAvailability(upd);
                                                }}
                                                className="h-4 w-4 accent-orange-500"
                                            />
                                            <span className={`text-sm font-semibold ${day.is_available ? 'text-gray-800' : 'text-gray-400'}`}>
                                                {day.day_name}
                                            </span>
                                        </div>
                                        {day.is_available ? (
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="time"
                                                    value={day.start_time}
                                                    onChange={e => {
                                                        const upd = [...storeAvailability];
                                                        upd[idx] = { ...upd[idx], start_time: e.target.value };
                                                        setStoreAvailability(upd);
                                                    }}
                                                    className="h-8 w-28 text-sm"
                                                />
                                                <span className="text-gray-400 text-sm">→</span>
                                                <Input
                                                    type="time"
                                                    value={day.end_time}
                                                    onChange={e => {
                                                        const upd = [...storeAvailability];
                                                        upd[idx] = { ...upd[idx], end_time: e.target.value };
                                                        setStoreAvailability(upd);
                                                    }}
                                                    className="h-8 w-28 text-sm"
                                                />
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400 italic">Fermé</span>
                                        )}
                                    </div>
                                ))}
                                <Button
                                    onClick={handleSaveStoreAvailability}
                                    disabled={savingAvailability}
                                    className="bg-orange-500 hover:bg-orange-600 mt-2"
                                >
                                    {savingAvailability ? "Sauvegarde..." : "💾 Sauvegarder les horaires"}
                                </Button>
                            </CardContent>
                        </Card>

                        {/* 4.5 Status & Danger Zone */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Statut & Zone de Danger</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <Label className="text-base">Mode Vacance</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Désactive temporairement la prise de rendez-vous.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={settingsForm.vacation_mode}
                                        onCheckedChange={(checked) => setSettingsForm({ ...settingsForm, vacation_mode: checked })}
                                    />
                                </div>

                                <div className="border-t pt-6 mt-4">
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="destructive" className="w-full">
                                                <Trash2 className="mr-2 h-4 w-4" />
                                                Supprimer définitivement la boutique
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Cette action est irréversible. Elle supprimera définitivement votre boutique, vos services et l&apos;historique de vos rendez-vous.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                                <AlertDialogAction onClick={handleDeleteStore} className="bg-red-600 hover:bg-red-700">
                                                    Confirmer la suppression
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:static md:bg-transparent md:border-t-0 md:shadow-none md:p-0">
                            <div className="container max-w-7xl mx-auto flex justify-end">
                                <Button onClick={handleSaveSettings} size="lg" className="w-full md:w-auto shadow-xl">
                                    <SettingsIcon className="mr-2 h-4 w-4" />
                                    Sauvegarder tout
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                </Tabs>
            </main>
        </div >
    );
}
